import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from '../components/Layout/Modal';
import { getPricingPlans } from '../services/pricingPlans';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: ${props => props.theme.background};
`;

const PricingSection = styled.div`
  padding: 6rem 1rem 2rem 1rem; /* Added top padding for navbar */
  text-align: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const PricingTitle = styled.h2`
  color: ${props => props.theme.text};
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const PricingSubtitle = styled.p`
  color: ${props => props.theme.textMuted};
  font-size: 1.125rem;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  max-width: 1000px;
  margin: 0 auto;
`;

const PricingCard = styled.div`
  background: ${props => props.theme.background};
  border: 2px solid ${props => props.popular ? '#5865f2' : props.theme.border};
  border-radius: 12px;
  padding: 2rem;
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: #5865f2;
  color: ${props => props.theme.text};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const PlanName = styled.h3`
  color: ${props => props.theme.text};
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const PlanPrice = styled.div`
  color: ${props => props.theme.text};
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const PlanPeriod = styled.div`
  color: ${props => props.theme.textMuted};
  font-size: 1rem;
  margin-bottom: 1.5rem;
`;

const PlanFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
`;

const PlanFeature = styled.li`
  color: ${props => props.theme.textSecondary};
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
  padding-left: 1.5rem;
  position: relative;

  &:before {
    content: '✓';
    color: #5865f2;
    font-weight: bold;
    position: absolute;
    left: 0;
  }
`;

const FreeTierNote = styled.div`
  max-width: 50%;
  margin: 0 auto;
  text-align: center;
  color: ${props => props.theme.textSecondary};
  font-size: 0.875rem;
  margin-top: 1rem;
  font-style: italic;
`;

const PricingBanner = styled.div`
  width: auto;
  margin: 2rem auto;
  background: linear-gradient(90deg, #ffe5e5 0%, #ffebf0 100%);
  border: 1px solid #ffb6c1;
  color: #923b4c;
  padding: 1rem 1.25rem;
  margin-top: calc(1.25rem + 5.5rem);
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 5.5rem;
  z-index: 10;
`;

const BannerText = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.4;
  max-width: 78%;
`;

const BannerLink = styled.button`
  color: #8f2e4c;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.95rem;
  margin-left: 1rem;
  background: transparent;
  border: none;
  padding: 0;

  &:hover {
    color: #6c1f39;
  }
`;

const DisclaimerText = styled.p`
  color: ${props => props.theme.text};
  line-height: 1.5;
  margin-bottom: 1rem;
`;


const Pricing = () => {
  const [pricingPlans, setPricingPlans] = useState(null);
  const [isDisclaimerOpen, setDisclaimerOpen] = useState(false);

  useEffect(() => {
    const fetchPricingPlans = async () => {
      try {
        const plansData = await getPricingPlans();
        setPricingPlans(plansData);
      } catch (error) {
        console.error('🚨 Error fetching pricing plans:', error);
        setPricingPlans(null);
      }
    };

    fetchPricingPlans();
  }, []);

  return (
    <PageContainer>
      <PricingBanner>
        <BannerText>
          Removal Notice of AI Plans
        </BannerText>
        <BannerLink onClick={() => setDisclaimerOpen(true)}>
          View more
        </BannerLink>
      </PricingBanner>

      {pricingPlans && Object.keys(pricingPlans).length > 0 ? (
        <PricingSection>
          <PricingTitle>Choose Your Plan</PricingTitle>
          <PricingSubtitle>
            Start with our free plan or upgrade for additional capacity and advanced features to boost your productivity.
          </PricingSubtitle>
          <PricingGrid>
            {/* Free Plan */}
            <PricingCard>
              <PlanName>Free</PlanName>
              <PlanPrice>$0</PlanPrice>
              <PlanPeriod>Forever</PlanPeriod>
              <PlanFeatures>
                <PlanFeature>Single semester planning*</PlanFeature>
                <PlanFeature>Unlimited assignments</PlanFeature>
                <PlanFeature>3 Flashcard decks</PlanFeature>
                <PlanFeature>250 Flashcards max**</PlanFeature>
                <PlanFeature style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem' }}>File uploads supported for notes (1MB max)</PlanFeature>
              </PlanFeatures>
            </PricingCard>

            {/* Monthly Plan */}
            {pricingPlans.monthly && (
              <PricingCard popular>
                <PopularBadge>Most Popular</PopularBadge>
                <PlanName>Monthly</PlanName>
                <PlanPrice>${(pricingPlans.monthly.price / 100).toFixed(2)}</PlanPrice>
                <PlanPeriod>billed monthly</PlanPeriod>
                <PlanFeatures>
                  <PlanFeature><strong>Everything in Free</strong></PlanFeature>
                  <PlanFeature><strong>Multi-semester planning</strong></PlanFeature>
                  <PlanFeature><strong>Unlimited Flashcard decks</strong></PlanFeature>
                  <PlanFeature><strong>Unlimited Flashcards per deck</strong></PlanFeature>
                  <PlanFeature style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem', fontWeight: 'bold' }}>File uploads supported for notes (50MB max)</PlanFeature>
                </PlanFeatures>
              </PricingCard>
            )}

            {/* Yearly Plan */}
            {pricingPlans.yearly && (
              <PricingCard>
                <PlanName>Yearly</PlanName>
                <PlanPrice>${(pricingPlans.yearly.price / 100).toFixed(2)}</PlanPrice>
                <PlanPeriod>billed yearly</PlanPeriod>
                <PlanFeatures>
                  <PlanFeature><strong>Everything in Free</strong></PlanFeature>
                  <PlanFeature><strong>Multi-semester planning</strong></PlanFeature>
                  <PlanFeature><strong>Unlimited Flashcard decks</strong></PlanFeature>
                  <PlanFeature><strong>Unlimited Flashcards per deck</strong></PlanFeature>
                  <PlanFeature><strong>3 months free*</strong></PlanFeature>
                  <PlanFeature style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem' }}>File uploads supported for notes (50MB max)</PlanFeature>
                </PlanFeatures>
                <h3 style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.5rem' }}>
                  * Based on monthly plan pricing<br />
                  ** Flashcard limits are totals, there are no per deck limits
                </h3>
              </PricingCard>
            )}
          </PricingGrid>
          <FreeTierNote>
            *Single semester planning on free plan is limited to 5 active courses. If you mark a course as completed, it will free up space for another active course. This allows you to still continue to your following semesters, but only one at a time.
            If you want to plan multiple semesters at once, you will have to upgrade to the monthly, quarterly, or yearly plans. <br />NOTE: The Quarterly plan is hidden on the pricing page, but is available once you create an account and log in.
          </FreeTierNote>
        </PricingSection>
      ) : (
        <PricingSection>
          <PricingTitle>Loading Plans...</PricingTitle>
        </PricingSection>
      )}

      <Modal
        isOpen={isDisclaimerOpen}
        onClose={() => setDisclaimerOpen(false)}
        title="AI Removal Notice"
        size="medium"
      >
        <DisclaimerText>
          To keep our core premium tiers affordable and sustainable, we have temporarily (potentially long-term) removed our AI plans. The rising operational costs of AI APIs meant we couldn't keep 
          offering those features without drastically hiking prices for everyone.
        </DisclaimerText>
        <DisclaimerText>
          We are actively looking into more cost-effective, sustainable AI models so we can bring these features back in the future without compromising the platform.
        </DisclaimerText>
        <hr style={{ border: `1px solid ${props => props.theme.border}`, margin: '1.5rem 0' }} />
        <DisclaimerText>
          <i>Thank you for your support and understanding! If you have any thoughts, suggestions, or questions, feel free to reach out to our support team.</i>
        </DisclaimerText>
      </Modal>

    </PageContainer>
  );
};

export default Pricing;
